import {
  asHealthcareServiceId,
  asScheduleBlockId,
  asScheduleId,
  type HealthcareServiceId,
  type ScheduleBlockId,
  type ScheduleId,
} from "@repo/domain";
import { z } from "zod";
import { newEntityIdSchema } from "./entity-id.schemas";

export const scheduleIdSchema: z.ZodType<ScheduleId, z.ZodTypeDef, string> = z.string().min(1).transform(asScheduleId);
export const scheduleBlockIdSchema: z.ZodType<ScheduleBlockId, z.ZodTypeDef, string> = z
  .string()
  .min(1)
  .transform(asScheduleBlockId);

/** Cached set of valid IANA timezone names */
const validTimezones = new Set(Intl.supportedValuesOf("timeZone"));

/** Zod schema that validates IANA timezone strings */
const timezoneSchema = z.string().refine((tz) => validTimezones.has(tz), { message: "Invalid IANA timezone" });

const codedValueSchema = z.object({
  system: z.string().min(1),
  code: z.string().min(1),
  display: z.string().optional(),
});

/** Backward-compatible shared schema for generic FHIR inputs in other domains */
const codingSchema = z.object({
  system: z.string().optional(),
  code: z.string().optional(),
  display: z.string().optional(),
});

/** Backward-compatible shared schema for generic FHIR inputs in other domains */
const codeableConceptSchema = z.object({
  coding: z.array(codingSchema).optional(),
  text: z.string().optional(),
});

const actorRefSchema = z
  .string()
  .regex(/^(Practitioner|Location|Device)\/.+$/, "actorRef must be Practitioner/<id>, Location/<id>, or Device/<id>");

/** Period schema */
const periodSchema = z
  .object({
    start: z.string().optional(),
    end: z.string().optional(),
  })
  .refine((p) => !p.start || !p.end || new Date(p.start) <= new Date(p.end), {
    message: "Period start must be before or equal to end",
  });

/** Export schemas for external use */
export { actorRefSchema, codedValueSchema, codeableConceptSchema, codingSchema };

// === Scheduling Parameters Schemas ===

/** Duration with unit */
const schedulingDurationSchema = z.object({
  value: z.number().positive(),
  unit: z.enum(["min", "h"]),
});

/** Day of week */
const dayOfWeekSchema = z.enum(["mon", "tue", "wed", "thu", "fri", "sat", "sun"]);

/** Availability timing */
const availabilityTimingSchema = z
  .object({
    dayOfWeek: z.array(dayOfWeekSchema).min(1),
    startTime: z.string().regex(/^\d{2}:\d{2}$/, "Must be HH:mm format"),
    endTime: z.string().regex(/^\d{2}:\d{2}$/, "Must be HH:mm format"),
  })
  .refine(
    (t) =>
      // Allow "00:00"/"00:00" sentinel for "always available"
      (t.startTime === "00:00" && t.endTime === "00:00") || t.startTime < t.endTime,
    { message: "Availability start time must be before end time" },
  );

/** Booking advance limit duration (days or weeks) */
const bookingAdvanceDurationSchema = z.object({
  value: z.number().positive(),
  unit: z.enum(["d", "wk"]),
});

/** Booking limit — caps per period */
const bookingLimitSchema = z.object({
  maxPerPeriod: z.number().positive().int(),
  periodUnit: z.enum(["d", "wk"]),
});

/** Scheduling parameters (one per HealthcareService) */
export const healthcareServiceIdSchema: z.ZodType<HealthcareServiceId, z.ZodTypeDef, string> = z
  .string()
  .min(1)
  .transform(asHealthcareServiceId);

const schedulingParametersSchema = z.object({
  /** HealthcareService id these params apply to (Medplum 5.1.11 required). */
  serviceId: healthcareServiceIdSchema,
  duration: schedulingDurationSchema,
  timezone: timezoneSchema.optional(),
  availability: availabilityTimingSchema.optional(),
  bufferBefore: schedulingDurationSchema.optional(),
  bufferAfter: schedulingDurationSchema.optional(),
  alignmentInterval: schedulingDurationSchema.optional(),
  alignmentOffset: schedulingDurationSchema.optional(),
  bookingAdvanceLimit: bookingAdvanceDurationSchema.optional(),
  bookingLimit: z.array(bookingLimitSchema).optional(),
});

export { schedulingParametersSchema, schedulingDurationSchema, bookingLimitSchema, bookingAdvanceDurationSchema };
export type SchedulingParameters = z.infer<typeof schedulingParametersSchema>;

// === Schedule Schemas ===

export const createScheduleInputSchema = z.object({
  id: newEntityIdSchema,
  active: z.boolean().default(true),
  actorRef: actorRefSchema,
  planningHorizon: periodSchema.optional(),
  serviceCategory: z.array(codedValueSchema).optional(),
  serviceType: z.array(codedValueSchema).optional(),
  specialty: z.array(codedValueSchema).optional(),
  comment: z.string().optional(),
  /** Actor's availability window. Handler fans out across all active protocol HSs when set. */
  availability: availabilityTimingSchema.optional(),
  /** Actor's timezone. Applied to every auto-derived SP entry. */
  timezone: timezoneSchema.optional(),
  /** Explicit per-HS SP entries. Legacy path — if set, overrides the auto-fan-out. */
  schedulingParameters: z.array(schedulingParametersSchema).optional(),
  initialBlocks: z
    .array(
      z.object({
        blockType: z.enum(["one-time", "recurring"]).default("one-time"),
        start: z.string(),
        end: z.string(),
        reason: z.string().optional(),
        recurring: z
          .object({
            frequency: z.enum(["daily", "weekly"]),
            dayOfWeek: z.array(z.enum(["mon", "tue", "wed", "thu", "fri", "sat", "sun"])).optional(),
            startDate: z.string().optional(),
            endDate: z.string().optional(),
          })
          .optional(),
        serviceType: codedValueSchema.optional(),
      }),
    )
    .optional(),
});
export type CreateScheduleInput = z.infer<typeof createScheduleInputSchema>;

export const updateScheduleInputSchema = z.object({
  id: scheduleIdSchema,
  active: z.boolean().optional(),
  planningHorizon: periodSchema.optional(),
  serviceCategory: z.array(codedValueSchema).optional(),
  serviceType: z.array(codedValueSchema).optional(),
  specialty: z.array(codedValueSchema).optional(),
  comment: z.string().optional(),
  /** Apply the actor’s working pattern to every service on the schedule. */
  availability: availabilityTimingSchema.optional(),
  timezone: timezoneSchema.optional(),
  schedulingParameters: z.array(schedulingParametersSchema).optional(),
});
export type UpdateScheduleInput = z.infer<typeof updateScheduleInputSchema>;

export const deleteScheduleInputSchema = z.object({
  id: scheduleIdSchema,
});
export type DeleteScheduleInput = z.infer<typeof deleteScheduleInputSchema>;

// === Availability Blocking Schemas ===

/** Block type: one-time (specific dates) or recurring pattern */
const blockTypeSchema = z.enum(["one-time", "recurring"]);

/** Recurrence frequency */
const recurrenceFrequencySchema = z.enum(["daily", "weekly"]);

/** Recurring pattern for blocked time */
export const recurringPatternSchema = z.object({
  frequency: recurrenceFrequencySchema,
  dayOfWeek: z.array(dayOfWeekSchema).optional(),
  startDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Must be YYYY-MM-DD format")
    .optional(),
  endDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Must be YYYY-MM-DD format")
    .optional(),
});
export type RecurringPatternInput = z.infer<typeof recurringPatternSchema>;

export const blockAvailabilityInputSchema = z
  .object({
    scheduleId: scheduleIdSchema,
    blockType: blockTypeSchema.default("one-time"),
    start: z.string(), // HH:mm for recurring, ISO datetime for one-time
    end: z.string(),
    reason: z.string().optional(),
    recurring: recurringPatternSchema.optional(),
    serviceType: codedValueSchema.optional(),
  })
  .refine(
    (b) => {
      // For recurring blocks, compare HH:mm strings lexicographically
      if (b.blockType === "recurring") return b.start < b.end;
      // For one-time blocks, compare as ISO datetimes
      return new Date(b.start) < new Date(b.end);
    },
    { message: "Block start must be before end" },
  );
export type BlockAvailabilityInput = z.infer<typeof blockAvailabilityInputSchema>;

export const unblockAvailabilityInputSchema = z.object({
  scheduleId: scheduleIdSchema,
  blockId: scheduleBlockIdSchema,
});
export type UnblockAvailabilityInput = z.infer<typeof unblockAvailabilityInputSchema>;
